/*
  LP Product: buy box, gallery thumbnails and video reviews.

  Buy box flow: pick a supply tier → pick subscribe / buy once → pick free bottles → add to cart.
  The paid supply goes in as a Skio Dynamic Box, built the same way as the PDP
  (assets/product-pdp-buybox-d-single.js) from the tier variant's box metafields: single-bottle
  lines with the box properties, on the box selling plan when subscribing, or with
  _selling_plan_id kept as a property when buying once.
  Free bottles go in as separate one-time lines with the _lp_free_gift property; the free gift
  app makes them free. Add to cart empties the cart first, so the order holds only this offer,
  but only when the cart may hold something (see cartMayHaveItems), so an empty cart costs no
  extra request. Prices are rendered server-side per tier; JS only toggles which tier's
  copy is visible and handles the free bottle picker.
*/

(() => {
  const fill = (template, tokens) =>
    String(template || '').replace(/\[([A-Za-z_]+)\]/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : match
    );

  // Dynamic Box lines for one tier, same shape as the PDP adds. Every line also carries
  // _lp_offer_id, so lp-cart.js keeps the free bottles only while the box is in the cart.
  const boxLines = (box, purchase, offerId) => {
    const list = (value) => (typeof value === 'string' ? value.split(',') : value || []);
    const ids = list(box.ids);
    const plans = list(box.plans);
    const qtys = list(box.qtys);
    const uniqId = Date.now().toString();
    const shared = {
      _dynamicBoxId: box.dynbox,
      _dynamicBoxIndex: 0,
      _custom_product_title: box.product_title,
      _custom_image: box.custom_image,
      _custom_title: box.custom_title,
      _custom_serving: box.custom_servings,
      _custom_price: box.custom_price,
      _custom_compare_price: box.custom_compare_price,
      _custom_sub_price: box.custom_sub_price,
      _custom_sub_compare_price: box.custom_sub_compare_price,
      _custom_quantity: box.custom_quantity,
      _custom_quantity_group: box.qtys,
      _customSellingPlanName: box.custom_selling_plan,
    };
    return ids.map((id, index) => {
      const qty = parseInt(qtys[index], 10) || 1;
      const plan = parseInt(plans[index], 10);
      const properties = {
        ...shared,
        _base_qty: qty.toString(),
        _role: index === 0 ? 'main' : 'hidden',
        _uniqBundleId: uniqId,
        _lp_offer_id: offerId,
      };
      const line = { id: String(id).trim(), quantity: qty, properties };
      if (purchase === 'subscribe' && plan) line.selling_plan = plan;
      if (purchase !== 'subscribe' && plan) properties._selling_plan_id = plan;
      return line;
    });
  };

  if (!customElements.get('lp-buy-box')) {
    class LpBuyBox extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        try {
          this.config = JSON.parse(this.querySelector('[data-lp-config]').textContent);
        } catch (error) {
          console.error('[lp-buy-box] config parse error', error);
          return;
        }

        this.tiers = this.config.tiers || [];
        this.gifts = this.config.gifts || [];
        this.text = this.config.text || {};
        this.picks = new Map();
        this.attention = false;
        // Set when the button was pressed before every free bottle was picked.
        this.missing = false;

        this.tierInputs = [...this.querySelectorAll('[data-tier-input]')];
        this.purchaseInputs = [...this.querySelectorAll('[data-purchase-input]')];
        this.giftsEl = this.querySelector('[data-gifts]');
        this.giftTiles = [...this.querySelectorAll('[data-gift]')];
        this.cta = this.querySelector('[data-add-to-cart]');
        this.ctaText = this.querySelector('[data-cta-text]');
        this.errorEl = this.querySelector('[data-cart-error]');
        // Item count when the page was rendered (layout passes cart.item_count).
        this.renderedCartCount = Number(this.dataset.cartCount) || 0;

        const params = new URLSearchParams(window.location.search);
        const defaultInput = this.tierInputs.find((input) => input.hasAttribute('data-default'));
        let tierIndex = defaultInput ? Number(defaultInput.value) : this.tiers[0]?.index ?? 0;
        const tierParam = Number(params.get('tier'));
        if (tierParam && this.tiers.some((tier) => tier.index === tierParam - 1)) tierIndex = tierParam - 1;

        this.tierIndex = tierIndex;
        this.purchase = params.get('purchase') === 'once' ? 'once' : 'subscribe';

        this.tierInputs.forEach((input) => {
          input.addEventListener('change', () => this.setTier(Number(input.value)));
        });
        this.purchaseInputs.forEach((input) => {
          input.addEventListener('change', () => this.setPurchase(input.value));
        });
        this.giftTiles.forEach((tile) => {
          const index = Number(tile.dataset.gift);
          const toggle = tile.querySelector('[data-gift-toggle]');
          toggle.addEventListener('click', () => this.toggleGift(index));
          // The rest of the card (image, copy, empty space) toggles too; the toggle button
          // and the quantity stepper handle their own clicks.
          tile.addEventListener('click', (event) => {
            if (event.target.closest('[data-gift-toggle], .lp-gift__stepper')) return;
            this.toggleGift(index);
          });
          // layout/theme.liquid clicks e.target on every Enter keydown; buttons already click
          // themselves on Enter, so stop the second click (undone toggle, +2 quantity).
          tile.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && event.target.closest('button')) event.stopPropagation();
          });
          tile.querySelector('[data-gift-plus]').addEventListener('click', () => this.changeGift(index, 1));
          tile.querySelector('[data-gift-minus]').addEventListener('click', () => this.changeGift(index, -1));
        });
        this.cta.addEventListener('click', () => this.submit());

        this.render();
      }

      get tier() {
        return this.tiers.find((tier) => tier.index === this.tierIndex) || this.tiers[0];
      }

      get allowance() {
        return this.giftTiles.length ? this.tier.freeCount || 0 : 0;
      }

      get picked() {
        let total = 0;
        this.picks.forEach((qty) => (total += qty));
        return total;
      }

      get remaining() {
        return Math.max(this.allowance - this.picked, 0);
      }

      setTier(index) {
        this.tierIndex = index;
        if (!this.tier.planId) this.purchase = 'once';
        this.trimPicks();
        this.render();
      }

      setPurchase(value) {
        if (value === 'subscribe' && !this.tier.planId) return;
        this.purchase = value;
        this.render();
      }

      // Drop picks beyond the new tier's allowance, newest first.
      trimPicks() {
        let over = this.picked - this.allowance;
        const entries = [...this.picks.entries()].reverse();
        for (const [index, qty] of entries) {
          if (over <= 0) break;
          const cut = Math.min(qty, over);
          if (qty - cut > 0) this.picks.set(index, qty - cut);
          else this.picks.delete(index);
          over -= cut;
        }
      }

      toggleGift(index) {
        this.attention = true;
        const qty = this.picks.get(index) || 0;
        if (this.allowance === 1) {
          // Single pick behaves like a radio group.
          this.picks.clear();
          if (!qty) this.picks.set(index, 1);
        } else if (qty) {
          this.picks.delete(index);
        } else if (this.remaining > 0) {
          this.picks.set(index, 1);
        }
        this.render();
      }

      changeGift(index, delta) {
        this.attention = true;
        const qty = this.picks.get(index) || 0;
        if (delta > 0 && this.remaining <= 0) return;
        const next = Math.max(qty + delta, 0);
        if (next) this.picks.set(index, next);
        else this.picks.delete(index);
        this.render();
      }

      render() {
        const tier = this.tier;
        if (!tier) return;
        const purchase = tier.planId ? this.purchase : 'once';

        this.querySelectorAll('[data-tier-card]').forEach((card) => {
          const selected = Number(card.dataset.tierCard) === tier.index;
          card.classList.toggle('is-selected', selected);
          card.querySelector('[data-tier-input]').checked = selected;
        });

        this.purchaseInputs.forEach((input) => {
          const selected = input.value === purchase;
          input.checked = selected;
          input.closest('[data-purchase-option]').classList.toggle('is-selected', selected);
          if (input.value === 'subscribe') {
            input.disabled = !tier.planId;
            input.closest('[data-purchase-option]').hidden = !tier.planId;
          }
        });

        this.querySelectorAll('[data-tier-pane], [data-purchase-pane]').forEach((el) => {
          const tierOk = !('tierPane' in el.dataset) || Number(el.dataset.tierPane) === tier.index;
          const purchaseOk = !('purchasePane' in el.dataset) || el.dataset.purchasePane === purchase;
          el.hidden = !(tierOk && purchaseOk);
        });

        this.renderGifts(purchase);
        this.renderCta(purchase);
      }

      formatMoney(cents) {
        const amount = cents / 100;
        try {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: this.config.currency || 'USD',
            minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
          }).format(amount);
        } catch (e) {
          return `$${amount}`;
        }
      }

      renderGifts(purchase) {
        if (!this.giftsEl) return;
        const allowance = this.allowance;
        const remaining = this.remaining;
        const picked = this.picked;
        const done = allowance > 0 && remaining === 0;

        this.giftsEl.hidden = allowance === 0;
        this.giftsEl.dataset.mode = allowance > 1 ? 'multi' : 'single';
        this.giftsEl.classList.toggle('is-attention', this.attention && !done);
        if (done || allowance === 0) this.missing = false;
        this.giftsEl.classList.toggle('is-missing', this.missing);
        // Once every bottle is picked the heading turns green and the prompt hides (CSS).
        this.giftsEl.classList.toggle('is-done', done);

        // Value tag: "$13+ Value" while picking, then the picked bottles' actual value.
        const valueTag = this.giftsEl.querySelector(`[data-tier-pane="${this.tier.index}"] [data-gift-value-tag]`);
        if (valueTag) {
          if (!valueTag.dataset.original) valueTag.dataset.original = valueTag.textContent;
          let pickedValue = 0;
          this.picks.forEach((qty, index) => {
            const gift = this.gifts.find((g) => g.index === index);
            pickedValue += (gift?.value || 0) * qty;
          });
          valueTag.textContent =
            done && pickedValue > 0 && this.text.valueTagDone
              ? fill(this.text.valueTagDone, { gift_value: this.formatMoney(pickedValue) })
              : valueTag.dataset.original;
        }

        this.giftTiles.forEach((tile) => {
          const qty = this.picks.get(Number(tile.dataset.gift)) || 0;
          tile.classList.toggle('is-selected', qty > 0);
          tile.querySelector('[data-gift-toggle]').setAttribute('aria-pressed', String(qty > 0));
          tile.querySelector('[data-gift-qty]').textContent = qty;

          const minus = tile.querySelector('[data-gift-minus]');
          minus.disabled = qty === 0;
          minus.querySelector('[data-icon="delete-disabled"]').hidden = qty !== 0;
          minus.querySelector('[data-icon="delete"]').hidden = qty !== 1;
          minus.querySelector('[data-icon="remove"]').hidden = qty < 2;

          const plus = tile.querySelector('[data-gift-plus]');
          plus.disabled = remaining === 0;
          plus.querySelector('[data-icon="add"]').hidden = remaining === 0;
          plus.querySelector('[data-icon="add-disabled"]').hidden = remaining !== 0;
        });

        const word = (count) => (count === 1 ? this.text.bottleSingular : this.text.bottlePlural);
        const capitalise = (value) => value.charAt(0).toUpperCase() + value.slice(1);
        const prompt = this.giftsEl.querySelector('[data-gift-prompt]');
        let template;
        if (done) template = this.text.promptDone;
        else if (picked === 0) template = allowance > 1 ? this.text.promptStartMany : this.text.promptStart;
        else template = this.text.promptMore;
        const promptWordCount = done ? allowance : remaining;
        prompt.querySelector('[data-prompt-text]').textContent = fill(template, {
          remaining,
          bottle_word: word(promptWordCount),
          Bottle_word: capitalise(word(promptWordCount)),
          is_are: allowance === 1 ? 'is' : 'are',
        });
        prompt.classList.toggle('is-done', done);
        // After a press with bottles still to pick, repeat the prompt in red under the button.
        if (this.errorEl && (this.missing || this.errorEl.dataset.pickError)) {
          this.errorEl.textContent = this.missing ? prompt.querySelector('[data-prompt-text]').textContent : '';
          this.errorEl.hidden = !this.missing;
          if (this.missing) this.errorEl.dataset.pickError = 'true';
          else delete this.errorEl.dataset.pickError;
        }
        prompt.querySelector('[data-prompt-icon="info"]').hidden = done || (this.attention && !done);
        prompt.querySelector('[data-prompt-icon="info-green"]').hidden = done || !this.attention;
        prompt.querySelector('[data-prompt-icon="done"]').hidden = !done;

        // Summary gift line: names the pick once everything is chosen.
        this.querySelectorAll(`[data-tier-pane="${this.tier.index}"] [data-gift-summary]`).forEach((el) => {
          if (!el.dataset.original) el.dataset.original = el.textContent;
          if (!done) {
            el.textContent = el.dataset.original;
            return;
          }
          let pickedLabel;
          if (picked === 1) {
            const [index] = [...this.picks.keys()];
            const gift = this.gifts.find((g) => g.index === index);
            pickedLabel = `1 FREE ${gift ? gift.shortName : word(1)}`;
          } else {
            pickedLabel = `${picked} FREE ${word(picked)}`;
          }
          const template =
            el.dataset.giftSummary === 'once' ? this.text.summaryGiftOnce : this.text.summaryGiftSubscribe;
          el.textContent = fill(template, { picked: pickedLabel });
        });
      }

      renderCta(purchase) {
        const tier = this.tier;
        let label;
        if (!tier.available) label = this.text.ctaSoldOut;
        else if (this.remaining > 0) {
          const word = this.remaining === 1 ? this.text.bottleSingular : this.text.bottlePlural;
          const tokens = {
            remaining: this.remaining,
            bottle_word: word,
            Bottle_word: word.charAt(0).toUpperCase() + word.slice(1),
          };
          if (this.picked === 0) label = fill(this.allowance > 1 ? this.text.ctaPickMany : this.text.ctaPickOne, tokens);
          else label = fill(this.text.ctaPickMore, tokens);
        } else label = purchase === 'subscribe' ? this.text.ctaSubscribe : this.text.ctaOnce;

        this.ctaText.textContent = label;
        this.cta.disabled = !tier.available;
      }

      flashGifts() {
        this.attention = true;
        this.missing = true;
        this.render();
        this.giftsEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.giftsEl.classList.remove('is-flashing');
        void this.giftsEl.offsetWidth; // restart the animation
        this.giftsEl.classList.add('is-flashing');
        clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => this.giftsEl.classList.remove('is-flashing'), 1000);
        this.giftsEl.querySelector('[data-gift-toggle]')?.focus({ preventScroll: true });
      }

      // True when the cart held items at render time, or any script on this page has changed
      // the cart since (the browser's resource timing entries record those requests).
      cartMayHaveItems() {
        if (this.renderedCartCount > 0) return true;
        return performance.getEntriesByType('resource').some((entry) => {
          try {
            return /\/cart\/(add|change|update)(\.js)?$/.test(new URL(entry.name).pathname);
          } catch (error) {
            return false;
          }
        });
      }

      async submit() {
        const tier = this.tier;
        if (!tier || !tier.available) return;
        if (this.remaining > 0) {
          this.flashGifts();
          return;
        }

        const purchase = tier.planId ? this.purchase : 'once';
        // Links the free bottles to this paid line; lp-cart.js removes them if the paid
        // line leaves the cart.
        const offerId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const items = tier.box
          ? boxLines(tier.box, purchase, offerId)
          : [{ id: tier.variantId, quantity: 1, properties: { _lp_offer_id: offerId } }];
        if (!tier.box && purchase === 'subscribe') items[0].selling_plan = tier.planId;
        if (purchase === 'subscribe' && items.some((item) => !item.selling_plan)) {
          // Never sell a subscription tier as a one-time purchase.
          console.error('[lp-buy-box] a subscription line has no selling plan', items);
          this.errorEl.textContent = this.text.error;
          this.errorEl.hidden = false;
          return;
        }

        const unconfigured = [...this.picks.keys()].some((index) => {
          const gift = this.gifts.find((g) => g.index === index);
          return !gift || !gift.variantId;
        });
        if (unconfigured) {
          // Never add the paid product in place of a free gift.
          console.error('[lp-buy-box] a picked free bottle has no gift variant ID');
          this.errorEl.textContent = this.text.error;
          this.errorEl.hidden = false;
          return;
        }

        this.picks.forEach((qty, index) => {
          const gift = this.gifts.find((g) => g.index === index);
          items.push({
            id: Number(gift.variantId),
            quantity: qty,
            properties: { _lp_free_gift: 'true', _lp_offer: tier.title, _lp_offer_id: offerId },
          });
        });

        const cartDrawer = document.querySelector('cart-drawer');
        // cart/add renders sections with the page's assigned template and ignores ?view=, so on
        // ?view= previews the drawer would render as on another template. There, request no
        // sections here and let LpCart.refreshDrawer fetch them from the current URL instead.
        const viewParam = new URLSearchParams(window.location.search).has('view');
        const sections = viewParam ? [] : cartDrawer?.getSectionsToRender?.().map((section) => section.section) || [];

        this.errorEl.hidden = true;
        this.cta.setAttribute('aria-busy', 'true');
        this.cta.disabled = true;
        cartDrawer?.enableLoading?.();

        let added = false;
        try {
          if (this.cartMayHaveItems()) {
            // Start from an empty cart. A failed clear shouldn't block the purchase.
            const cleared = await fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, {
              method: 'POST',
              headers: { Accept: 'application/json' },
            }).catch((error) => error);
            if (!cleared?.ok) console.warn('[lp-buy-box] could not clear the cart first', cleared);
          }

          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || response.statusText);

          document.dispatchEvent(
            new CustomEvent('lp:cart:added', { detail: { items, purchase, tier: tier.title } })
          );

          added = true;
          if (cartDrawer && typeof cartDrawer.open === 'function' && window.LpCart) {
            await window.LpCart.refreshDrawer(cartDrawer, data.sections || {});
            cartDrawer.open(this.cta);
          } else {
            window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
          }
        } catch (error) {
          console.error('[lp-buy-box] add to cart failed', error);
          if (added) {
            // The items are in the cart; only the drawer refresh failed. Go to the cart page.
            window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
            return;
          }
          this.errorEl.textContent = this.text.error;
          this.errorEl.hidden = false;
        } finally {
          this.cta.removeAttribute('aria-busy');
          this.cta.disabled = !tier.available;
          cartDrawer?.disableLoading?.();
        }
      }
    }

    customElements.define('lp-buy-box', LpBuyBox);
  }

  // ---------- Gallery: thumbnails scroll the mobile track ----------
  const initGallery = (gallery) => {
    if (gallery.dataset.ready) return;
    gallery.dataset.ready = 'true';
    const track = gallery.querySelector('[data-gallery-track]');
    const items = [...gallery.querySelectorAll('[data-gallery-item]')];
    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    if (!track || !thumbs.length) return;

    const select = (index) =>
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-selected', String(i === index)));
    select(0);

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.galleryThumb);
        const item = items[index];
        if (!item) return;
        track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        select(index);
      });
    });

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const left = track.scrollLeft;
          let closest = 0;
          let distance = Infinity;
          items.forEach((item, i) => {
            const d = Math.abs(item.offsetLeft - track.offsetLeft - left);
            if (d < distance) {
              distance = d;
              closest = i;
            }
          });
          select(closest);
        });
      },
      { passive: true }
    );
  };

  // ---------- Video reviews: play while visible, pause button, edge fade ----------
  const initSlider = (slider) => {
    if (slider.dataset.ready) return;
    slider.dataset.ready = 'true';
    const track = slider.querySelector('[data-slider-track]');
    const fade = slider.querySelector('.lp-reviews__fade');

    if (track && fade) {
      const updateFade = () => (fade.style.opacity = track.scrollLeft > 4 ? '1' : '0');
      track.addEventListener('scroll', updateFade, { passive: true });
      updateFade();
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    slider.querySelectorAll('.lp-review').forEach((review) => {
      const video = review.querySelector('video');
      const toggle = review.querySelector('[data-video-toggle]');
      if (!video || !toggle) return;
      video.muted = true;

      const setPlaying = (playing) => {
        toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        toggle.querySelector('[data-video-icon="pause"]').hidden = !playing;
        toggle.querySelector('[data-video-icon="play"]').hidden = playing;
      };
      video.addEventListener('play', () => setPlaying(true));
      video.addEventListener('pause', () => setPlaying(false));
      setPlaying(false);

      toggle.addEventListener('click', () => {
        if (video.paused) {
          review.dataset.userPaused = '';
          video.play().catch(() => {});
        } else {
          review.dataset.userPaused = 'true';
          video.pause();
        }
      });

      if (reduceMotion || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && review.dataset.userPaused !== 'true') {
              video.play().catch(() => {});
            } else if (!entry.isIntersecting && !video.paused) {
              video.pause();
            }
          });
        },
        { threshold: 0.5 }
      ).observe(review);
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-lp-gallery]').forEach(initGallery);
    root.querySelectorAll('[data-lp-slider]').forEach(initSlider);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
